import { make } from "vuex-pathify";
import loader from "@/util/loader";
import searchUtils from "@/util/searchUtils";
import get from "lodash/get";
import remove from "lodash/remove";
import keys from "lodash/keys";
import api from "Util/api";

const uriRoot = "/users";

const PRESET_LABEL_MAP = [
    { id: "newLeads", name: "New Leads (Last 2 Days)" },
    { id: "activeShoppers", name: "Active Shoppers (Last 14 Days)" },
    { id: "preQualifiedCustomers", name: "Finance Pre-Qualified Customers" },
    { id: "tradeInCustomers", name: "Customer with a Trade-In" },
    { id: "highIntentBuyers", name: "High Intent Buyers (Last 30 Days)" },
    { id: "recentlyActive", name: "Recently Active Customers (Last 3 Days)" },
    { id: "ownersSoldCustomers", name: "Sold Customers" },
    { id: "recentBuyersFollowUpCalls", name: "Recent Buyers (Last 30 Days)" },
    { id: "lowRemainingPayments", name: "Customers with 12 or Fewer Payments Remaining" },
    { id: "midTermOwnersEquityCheck", name: "Customers with 12–36 Payments Remaining" },
    { id: "tradeCycleCustomers", name: "Customers Who Purchased 2+ Years Ago" },
    { id: "dormantCustomers", name: "Inactive Customers (30+ Days)" },
    { id: "previouslyActiveNowCold", name: "Previously Active Customers (Inactive 14+ Days)" },
    { id: "noEngagementLeads", name: "Leads with No Website Activity" },
    { id: "vehicleInterest", name: "Customers Interested in a Specific Vehicle" },
];

// Parse URL filters once so we can derive dependent initial state from them.
const _initialFilters = searchUtils.parseFiltersFromUrl();
const _urlPredefined = _initialFilters.predefinedFilters;
const _initialPresetKeys = Array.isArray(_urlPredefined) ? [..._urlPredefined] : _urlPredefined ? [_urlPredefined] : [];

const initialState = {
    ...searchUtils.state(),
    // When preset filters are present in the URL, ensure the POSITIVE searchMethod is set
    // so the backend processes them correctly (parseSearchMethodsFromUrl only handles NEGATIVE).
    searchMethods: {
        ...searchUtils.state().searchMethods,
        ...(_initialPresetKeys.length > 0 ? { predefinedFilters: "POSITIVE" } : {}),
    },
    searchUri: null,
    initialLoad: true,
    pushHistoryEnabled: true,
    pageable: searchUtils.parsePageableFromUrl({
        sort: "createdDate,desc",
        page: 1,
    }),
    facets: null,
    filterNames: {
        predefinedFilters: {
            loader: { isLoading: false, isComplete: true },
            data: PRESET_LABEL_MAP,
        },
    },
    filters: _initialFilters,
    activeSavedFilterIds: [],
    activePresetKeys: _initialPresetKeys,
    presetFilterCounts: {},
    searchLoader: {
        loader: loader.defaultState(),
        data: [],
    },
    displayFields: [
        "firstName",
        "lastName",
        "stage",
        "preApprovalCount",
        "traits.preQualificationsCount",
        "traits.tradeInsCount",
        "traits.activeVehiclesInGarageCount",
        "traits.vehiclesInGarageCount",
        "traits.leadsCount",
        "traits.financeAppsSubmittedCount",
        "traits.salesCount",
        "lastLoginAt",
        "createdDate",
        "actions",
        "logins",
        "clientAdvisor",
        "websiteVisitsBeforeSignup",
        "tier",
    ],
    pageMetadata: {
        size: 0,
        totalElements: 0,
        totalPages: 0,
        number: 0,
    },
    /**
     * pills schema
     *      {
                "name": {
                    label: 'Name',
                    enabled: false,
                },
                "warrantyStatuses": {
                    label: 'Warranty',
                    enabled: true
                },
                "topDmas": {
                    type: 'range'
                }
            }
     *     label: label to use for display
     *     facet: the name of the search facet
     *     enabled: is the pill show be displayed
     *     type: the type of filter i.e. 'range' filter
     * }
     */
    pills: {
        dealerIds: {
            enabled: false,
        },
        generalSearch: {
            enabled: false,
        },
        name: {
            enabled: false,
        },
        emailOrPhone: {
            enabled: false,
        },
        city: {
            enabled: false,
        },
        vehicleOfInterest: {
            enabled: false,
        },
        createdDate: {
            enabled: false,
        },
        dmaCodes: {
            label: "DMAs",
            facet: "dmas",
        },
        topDmas: {
            type: "range",
        },
        programIds: {
            enabled: false,
        },
        predefinedFilters: {
            label: "Preset Filters",
        },
    },
    stageTiles: {
        data: null,
        loader: loader.defaultState(),
    },
    stageUsers: {
        data: null,
        loader: loader.defaultState(),
    },
    onlineUsers: {
        data: [],
        loader: loader.defaultState(),
    },
    inShowroomUsers: {
        data: [],
        loader: loader.defaultState(),
    },
    stageTitle: null,
};

const mutations = {
    ...make.mutations(initialState),
    ...searchUtils.mutations(),

    SET_STAGE_TITLE(state, stageTitle) {
        console.log(stageTitle);
        state.stageTitle = stageTitle;
    },

    SET_ACTIVE_PRESET_KEYS(state, keys) {
        state.activePresetKeys = keys;
    },

    SET_ACTIVE_SAVED_FILTER_IDS(state, ids) {
        state.activeSavedFilterIds = ids;
    },

    SET_PRESET_FILTER_COUNTS(state, counts) {
        state.presetFilterCounts = counts;
    },
};

const actions = {
    ...make.actions(initialState),
    ...searchUtils.actions(uriRoot, "User Search"),
    // Override searchUtils clearFilter to also reset preset/saved-filter UI state.
    clearFilter({ commit, dispatch }, filterName) {
        commit("CLEAR_FILTER", filterName);
        if (filterName === "predefinedFilters") {
            commit("SET_ACTIVE_PRESET_KEYS", []);
            commit("SET_ACTIVE_SAVED_FILTER_IDS", []);
        }
        dispatch("doSearch");
    },

    setSearchUri({ commit, state }, searchUri) {
        commit("SET_SEARCH_URI", searchUri);
    },
    fetchStageTiles({ commit, state }) {
        commit("SET_STAGE_TILES", {
            data: null,
            loader: loader.started(),
        });

        const body = { ...state.filters };
        body.searchMethods = state.searchMethods;
        body.includes = get(state, "fetchSource.includes", null);
        body.excludes = get(state, "fetchSource.excludes", null);

        let stageUrl = `${state.searchUri}/sales/stages-stats`;

        api.post(stageUrl, body)
            .then((response) => {
                let filterTiles = get(response, "data", null);
                remove(filterTiles.stages, function (n) {
                    return keys(n)[0] === "Needs Dealer";
                });
                commit("SET_STAGE_TILES", {
                    data: get(response, "data", null),
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                console.log(error);
                commit("SET_STAGE_TILES", {
                    data: null,
                    loader: loader.error(error),
                });
            });
    },
    fetchStageRecords({ commit, state }) {
        commit("SET_SEARCH_LOADER", {
            data: null,
            loader: loader.started(),
        });

        console.log(`in fetchStageRecords: ${state.stageTitle}`);

        const body = { ...state.filters };
        body.searchMethods = state.searchMethods;
        body.includes = get(state, "fetchSource.includes", null);
        body.excludes = get(state, "fetchSource.excludes", null);

        let baseUrl = `${state.searchUri}/${state.stageTitle}/search`;
        let queryUrl = `?page=${state.pageable.page}&sort=${state.pageable.sort}`;
        let stageUrl = `${baseUrl}${queryUrl}`;

        api.post(stageUrl, body)
            .then((response) => {
                console.log(response);
                console.log(response.data.content);
                commit("SET_SEARCH_LOADER", {
                    data: response.data.content,
                    loader: loader.successful(),
                });
                commit("SET_PAGE_METADATA", response.data.pageMetadata);
            })
            .catch((error) => {
                console.log(error);
                commit("SET_SEARCH_LOADER", {
                    data: null,
                    loader: loader.error(error),
                });
            });
    },

    fetchOnlineUsers({ commit }, dealerId) {
        commit("SET_ONLINE_USERS", {
            data: [],
            loader: loader.started(),
        });
        api.get(`/online-users/dealers/${dealerId}`)
            .then((response) => {
                commit("SET_ONLINE_USERS", {
                    data: response.data,
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                commit("SET_ONLINE_USERS", {
                    data: [],
                    loader: loader.error(),
                });
            });
    },
    fetchInShowroomUsers({ commit, rootState }, dealerId) {
        let url = `/showroom-users/dealers/${dealerId}`;
        if (rootState.loggedInUser.featureFlags["ATLAS_NEW_CUSTOMER_PAGE_IN_SHOWROOM_V2"]) {
            url = `/showroom-users/v2/dealers/${dealerId}`;
        }
        commit("SET_IN_SHOWROOM_USERS", {
            data: [],
            loader: loader.started(),
        });
        const result = api
            .get(url)
            .then((response) => {
                commit("SET_IN_SHOWROOM_USERS", {
                    data: response.data,
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                commit("SET_IN_SHOWROOM_USERS", {
                    data: [],
                    loader: loader.error(),
                });
            });

        return result;
    },

    clearFilters({ commit, state, dispatch }) {
        commit("SET_SEARCH_METHODS", {});
        // Replicate clearAllFilters logic: preserve dealerIds, programIds, programs
        const preserved = {};
        if (state.filters) {
            ["dealerIds", "programIds", "programs"].forEach((key) => {
                if (state.filters[key] !== undefined && state.filters[key] !== null) {
                    preserved[key] = state.filters[key];
                }
            });
        }
        commit("SET_FILTERS", preserved);
        commit("SET_ACTIVE_PRESET_KEYS", []);
        commit("SET_ACTIVE_SAVED_FILTER_IDS", []);
        dispatch("doSearch");
    },

    applyPresetFilter({ commit, state, dispatch }, { key, mode }) {
        const current = state.activePresetKeys || [];
        let next;

        if (mode === "only") {
            // Label click: select only this key
            next = [key];
        } else {
            // Checkbox click: toggle in/out; unchecking last clears all (back to "all checked")
            if (current.includes(key)) {
                next = current.filter((k) => k !== key);
            } else {
                next = [...current, key];
            }
        }

        commit("SET_ACTIVE_PRESET_KEYS", next);
        commit("SET_ACTIVE_SAVED_FILTER_IDS", []);
        // Mark as POSITIVE so the pill shows the label without "Removing:" prefix
        commit("SET_SEARCH_METHODS", next.length > 0 ? { predefinedFilters: "POSITIVE" } : {});
        // Ensure the filter name lookup is populated so pills resolve keys to display labels
        commit("SET_FILTER_NAMES", {
            filterNames: "predefinedFilters",
            loader: { isLoading: false, isComplete: true },
            data: PRESET_LABEL_MAP,
        });

        const clearedFilters = {};
        if (state.filters.dealerIds) clearedFilters.dealerIds = state.filters.dealerIds;
        if (state.filters.programIds) clearedFilters.programIds = state.filters.programIds;
        if (next.length > 0) clearedFilters.predefinedFilters = next;
        commit("SET_FILTERS", clearedFilters);
        dispatch("doSearch");
    },

    clearPresetFilters({ commit, state, dispatch }) {
        commit("SET_ACTIVE_PRESET_KEYS", []);
        commit("SET_SEARCH_METHODS", {});
        const clearedFilters = {};
        if (state.filters.dealerIds) clearedFilters.dealerIds = state.filters.dealerIds;
        if (state.filters.programIds) clearedFilters.programIds = state.filters.programIds;
        commit("SET_FILTERS", clearedFilters);
        dispatch("doSearch");
    },

    applySavedFilter({ commit, state, dispatch }, { savedFilterId, filters, allSavedItems, mode }) {
        const current = state.activeSavedFilterIds || [];
        let next;

        if (mode === "only") {
            next = [savedFilterId];
        } else {
            // toggle: remove if present, add if not; empty = all checked (no filter)
            next = current.includes(savedFilterId)
                ? current.filter((id) => id !== savedFilterId)
                : [...current, savedFilterId];
        }

        commit("SET_ACTIVE_SAVED_FILTER_IDS", next);

        // Merge filters from all active saved searches; reset duration fields per requirements
        // Strip internal SearchRequest fields that should not be restored as Vuex filters
        const STRIP_KEYS = [
            "withinCreatedDate",
            "withinLastActive",
            "withinMaturityDate",
            "includes",
            "excludes",
            "sort",
        ];
        let merged = {};
        let mergedSearchMethods = {};
        if (next.length > 0 && allSavedItems) {
            next.forEach((id) => {
                const item = allSavedItems.find((i) => i.savedFilterId === id);
                if (!item || !item.filters) return;
                const f = { ...item.filters };
                // Extract and accumulate searchMethods before stripping
                if (f.searchMethods && typeof f.searchMethods === "object") {
                    Object.assign(mergedSearchMethods, f.searchMethods);
                }
                delete f.searchMethods;
                STRIP_KEYS.forEach((k) => {
                    delete f[k];
                });
                // Drop null values so they don't pollute filters state
                Object.keys(f).forEach((k) => {
                    if (f[k] === null) delete f[k];
                });
                Object.assign(merged, f);
            });
        }
        if (state.filters.dealerIds) merged.dealerIds = state.filters.dealerIds;
        if (state.filters.programIds) merged.programIds = state.filters.programIds;

        // If the saved filter embeds predefined filters, ensure their searchMethod is POSITIVE.
        // Also restore all other saved searchMethods so filter chips show the correct polarity label.
        const savedPredefined = merged.predefinedFilters;
        if (Array.isArray(savedPredefined) && savedPredefined.length > 0) {
            commit("SET_SEARCH_METHODS", { ...mergedSearchMethods, predefinedFilters: "POSITIVE" });
            commit("SET_ACTIVE_PRESET_KEYS", savedPredefined);
        } else {
            commit("SET_ACTIVE_PRESET_KEYS", []);
            commit("SET_SEARCH_METHODS", mergedSearchMethods);
        }

        commit("SET_FILTERS", merged);
        dispatch("doSearch");
    },

    clearSavedFilters({ commit, state, dispatch }) {
        commit("SET_ACTIVE_SAVED_FILTER_IDS", []);
        commit("SET_SEARCH_METHODS", {});
        const preserved = {};
        if (state.filters.dealerIds) preserved.dealerIds = state.filters.dealerIds;
        if (state.filters.programIds) preserved.programIds = state.filters.programIds;
        commit("SET_FILTERS", preserved);
        dispatch("doSearch");
    },

    activateSavedFilter({ commit }, savedFilterId) {
        // Mark a saved filter as the sole active filter without re-running the search (used after save)
        commit("SET_ACTIVE_SAVED_FILTER_IDS", [savedFilterId]);
    },

    /**
     * Fetches the customer count for each predefined filter preset.
     * Calls POST /api/users/v2/facet_info?id=<presetId> for every preset ID,
     * using the current filters as the search body.
     * Results are stored in presetFilterCounts as { [presetId]: count }.
     */
    loadPresetCounts({ commit, state }, presetIds) {
        const searchUri = state.searchUri || "/users/v2";
        const body = { ...state.filters };
        body.searchMethods = state.searchMethods;

        const requests = presetIds.map((id) =>
            api
                .post(`${searchUri}/facet_info?id=${id}`, body)
                .then((response) => {
                    // results is a single object: { count, uniqueCount, id, name }
                    const results = get(response, "data.results", null);
                    const count = results != null ? results.count ?? results.uniqueCount ?? 0 : 0;
                    return { id, count };
                })
                .catch(() => ({ id, count: null }))
        );

        return Promise.all(requests).then((results) => {
            const counts = {};
            results.forEach(({ id, count }) => {
                counts[id] = count;
            });
            commit("SET_PRESET_FILTER_COUNTS", counts);
        });
    },

    /**
     * Loads the user's customer column preferences from the backend.
     * If the backend returns an empty list (no saved preference yet), the store
     * retains whatever is already in displayFields — which may have been
     * hydrated from localStorage by vuex-persistedstate.
     */
    loadColumnPreferences({ commit, state }) {
        return api
            .get("/users/column-preferences/customer-search")
            .then((response) => {
                const columnOrder = get(response, "data.columnOrder", []);
                if (columnOrder && columnOrder.length > 0) {
                    commit("SET_DISPLAY_FIELDS", columnOrder);
                }
                // If empty, keep the current displayFields (localStorage / default).
            })
            .catch(() => {
                // Network or auth failure — silently retain current displayFields.
            });
    },

    /**
     * Persists the user's customer column preferences to the backend.
     * The displayFields array (which encodes both selection and order) is sent as-is.
     * localStorage is kept in sync automatically by vuex-persistedstate.
     */
    saveColumnPreferences({ state }) {
        return api
            .post("/users/column-preferences/customer-search", {
                columnOrder: state.displayFields,
            })
            .catch(() => {
                // Save failure is non-fatal; localStorage already has the latest value.
            });
    },
};

const getters = {
    ...make.getters(initialState),
    ...searchUtils.getters(),
};

export default {
    namespaced: true,
    state: initialState,
    mutations,
    actions,
    getters,
};
